import { Texture } from 'three';
import {
    KintekiArrowModel,
    EntekiArrowModel,
    MakiwaraArrowModel,
    AllModel,
} from '@app/react/3d/ArrowModelInterface';
import {
    PartsProduct,
    PartsProductOptions,
    ShaftSurfaceTexture,
    PartsProductOptionsFeatherKindEnum as FK,
    MAKIWARA_THREAD_MODE,
} from '@app/react/const/type';
import { KintekiOrdermadeArrowModel } from '@app/react/3d/kintekiOrdermadeArrowModel';
import { EntekiOrdermadeArrowModel } from '@app/react/3d/entekiOrdermadeArrowModel';
import { MAKIWARA_THREAD_MODE_B, MAKIWARA_THREAD_MODE_C } from '../../const/variables';

export type Apply = (tex: Texture, options?: PartsProductOptions) => void;
export type Apply2 = (tex1: Texture, tex2: Texture, options?: PartsProductOptions) => void;
export type ApplyMultiple = (tex: Array<Texture>, options?: PartsProductOptions) => void;

export const shaft = (model: AllModel): Apply => (tex, options) => {
    const type = (options?.shaftSurfaceTexture || '') as ShaftSurfaceTexture;
    model.setShaft(tex, type);
};

export const kintekiHane = (
    model: KintekiArrowModel,
): Apply2 => (tex1, tex2) => model.setFeather(tex1, tex2);

export const entekiHane = (model: EntekiArrowModel): Apply2 => (tex1, tex2, options) => {
    model.setFeather(tex1, tex2);

    switch (options?.featherKind) {
        case FK.Normal:
            model.showFeather1();
            break;
        case FK.Kazakiri:
            model.showFeather2();
            break;
        case FK.Hyoutan:
            model.showFeather3();
            break;
        default:
            console.error('not match feather kind');
            break;
    }
};

export const makiwaraHane = (model: MakiwaraArrowModel): Apply2 => (tex1, tex2, options) => {
    model.setFeather(tex1, tex2);

    switch (options?.featherKind) {
        case FK.Normal:
            model.showFeather1();
            break;
        case FK.Hyoutan:
            model.showFeather3();
            break;
        default:
            console.error('not match feather kind');
            break;
    }

    model.modeA();
};

export const nonFeather = (model: MakiwaraArrowModel) => (mode: MAKIWARA_THREAD_MODE): void => {
    switch (mode) {
        case MAKIWARA_THREAD_MODE_B:
            model.modeB();
            break;
        case MAKIWARA_THREAD_MODE_C:
            model.modeC();
            break;
        default:
            console.error('got invalid thread mode', mode);
            break;
    }
    model.hideFeather();
};

export const thread = (model: AllModel, gloss: boolean): Apply => (tex) => {
    const type = gloss ? '' : 'matte';
    model.setThread(tex, type);
};

export const kebiki = (model: AllModel): Apply => (tex) => model.setKebiki(tex);

export const nock = (model: AllModel): Apply2 => (tex1, tex2, options) => {
    model.setNock(tex1, tex2);
    if (options?.nockKind === 'tenkyu') {
        model.showTenkyu();
        return;
    } if (options?.nockKind === 'fukumi') {
        model.showHukumi();
        return;
    }
    model.showHukumi();
};

export const arrowHead = (model: AllModel): Apply => (tex, options) => {
    model.setArrowhead(tex);

    if (
        model instanceof KintekiOrdermadeArrowModel
        || model instanceof EntekiOrdermadeArrowModel
    ) {
        if (options?.arrowheadKind === 'short') {
            model.showShortYajiri();
            return;
        } if (options?.arrowheadKind === 'long') {
            model.showLongYajiri();
            return;
        }
        model.showLongYajiri();
    }
};

export const twine = (model: AllModel): Apply2 => (tex1, tex2) => {
    model.setTwine(tex1);
    model.setTwineColor(tex2);
};

export const lame = (model: AllModel): Apply2 => (tex1, tex2) => {
    model.setLame(tex1);
    model.setLameColor(tex2);
};

export const familyCrest = (model: KintekiArrowModel): Apply => (tex) => model.setFamilycrest(tex);

export const familyCrestColor = (
    model: KintekiArrowModel,
): Apply => (tex) => model.setFamilycrestColor(tex);

export const carving = (
    model: AllModel,
    product: PartsProduct | null,
    carvingStr: string,
): void => {
    if (product === null) {
        model.writeVertical('');
        console.log('carving product not found');
        return;
    }

    const writeType = product.options?.carvingWritingType;
    const fontFamily = product.options?.carvingFontName;
    const writer = ((direction): typeof model.writeVertical => {
        if (direction === 1) {
            return model.writeVertical.bind(model);
        } if (direction === 2) {
            return model.writeHorizontal.bind(model);
        }
        console.error('carving: noting direction. recover vertical write');
        return model.writeVertical.bind(model);
    })(writeType);

    if (fontFamily === null) {
        console.error('carving: noting fontfamily. recerver default font');
        writer(carvingStr);
        return;
    }

    writer(carvingStr, fontFamily);
};

export const carvingColor = (model: AllModel): Apply => (tex) => model.setCarvingColor(tex);

export const decoration = (model: AllModel): ApplyMultiple => (texs) => {
    const { length } = texs;
    if (length === 2) {
        model.setDecoration(texs[0]);
        model.setDecorationColor(texs[1]);
        return;
    }

    if (length === 6) {
        model.setDecoration(texs[0], 0);
        model.setDecorationColor(texs[1], 0);
        model.setDecoration(texs[2], 1);
        model.setDecorationColor(texs[3], 1);
        model.setDecoration(texs[4], 2);
        model.setDecorationColor(texs[5], 2);
        return;
    }

    console.error('decoration texture file incorrect: size=', length);
};

export const featherinside = (model: AllModel): Apply2 => (tex1, tex2) => {
    model.setFeatherinside(tex1);
    model.setFeatherinsideColor(tex2);
};
